import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-US", {
      month: "short"
    }).toUpperCase();
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return "";
  }
}
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}
function JournalGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col">
            <div className={`${i === 0 ? "aspect-[4/5]" : "aspect-[16/10]"} w-full bg-card animate-pulse`} />
            <div className="mt-6 h-3 w-40 bg-card animate-pulse" />
            <div className="mt-4 h-6 w-full bg-card animate-pulse" />
            <div className="mt-2 h-6 w-3/4 bg-card animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm font-mono uppercase tracking-widest" style={{
      color: "var(--secondary)"
    }}>
        Unable to load tree health insights.
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-sm font-mono uppercase tracking-widest" style={{
      color: "var(--secondary)"
    }}>
        No insights published yet.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => {
      const isFeatured = i === 0;
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "ARBOR CARE";
      const dateLabel = formatDate(post.date);
      const excerpt = post.excerpt ? stripHtml(post.excerpt) : "";
      return <article data-index={i} key={post.id} className={`group flex flex-col ${isFeatured ? "lg:row-span-1" : ""}`}>
            <Link to={post.link || "#"} className="block overflow-hidden rounded-md">
              <AspectRatio ratio={isFeatured ? 4 / 5 : 16 / 10}>
                <div className="h-full w-full overflow-hidden bg-card">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-all duration-700 group-hover:scale-105 group-hover:brightness-110" /> : <div className="h-full w-full bg-card" />}
                </div>
              </AspectRatio>
            </Link>

            <div className="mt-7 flex flex-col">
              <div className="font-mono text-[11px] font-medium uppercase tracking-[0.2em]" style={{
            color: "var(--secondary)"
          }}>
                <span>{category.toUpperCase()}</span>
                {dateLabel && <>
                    <span className="mx-2 text-accent">—</span>
                    <span>{dateLabel}</span>
                  </>}
              </div>

              <h3 className={`font-default font-bold mt-4 leading-tight line-clamp-2 tracking-tight ${isFeatured ? "text-[26px] md:text-[32px]" : "text-[22px]"}`} style={{
            color: "var(--foreground)"
          }}>
                <Link to={post.link || "#"} className="bg-[length:0%_2px] bg-bottom bg-no-repeat bg-gradient-to-r from-accent to-accent transition-[background-size] duration-500 group-hover:bg-[length:100%_2px]">
                  {post.title}
                </Link>
              </h3>

              {excerpt && isFeatured && <p className="font-sans mt-4 text-[16px] leading-relaxed line-clamp-3" style={{
            color: "var(--secondary)"
          }}>
                  {excerpt}
                </p>}

              <div className="mt-6 flex flex-wrap items-center gap-6">
                <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] transition-all hover:text-accent" style={{
              color: "var(--foreground)"
            }} aria-label={`Read ${post.title}`}>
                  <span>View Analysis</span>
                  <ArrowUpRight className="h-3.5 w-3.5 text-accent" />
                </Link>
                <Link to="#" className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] transition-all hover:text-accent" style={{
              color: "var(--secondary)"
            }}>
                  <span>Residential Care</span>
                  <ArrowRight className="h-3.5 w-3.5 text-accent" />
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function TreeHealthInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="3562" id="tree-health-insights" className="w-full bg-background text-foreground py-20 md:py-28 lg:py-32">
      <h1 className="sr-only">Expert Tree Health Insights for DFW Homeowners</h1>
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between md:gap-12">
          <div className="flex flex-col md:max-w-2xl">
            <span className="font-mono text-[12px] font-medium uppercase tracking-[0.3em]" style={{
            color: "var(--secondary)"
          }}>
              ARBOR PRO INTEL — 04
            </span>
            <h2 className="font-default font-bold mt-6 text-[42px] leading-[0.95] tracking-tighter md:text-[52px] lg:text-[64px]" style={{
            color: "var(--foreground)"
          }}>
              Tree Health
              <br />
              & Insights.
            </h2>
          </div>

          <div className="md:pb-3">
            <Button asChild variant="ghost" className="group h-auto bg-transparent p-0 font-mono text-[12px] font-bold uppercase tracking-[0.2em] hover:bg-transparent hover:text-accent" style={{
            color: "var(--foreground)"
          }}>
              <Link to="/TreeInsightsPage" className="inline-flex items-center gap-2">
                <span>Explore all reports</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-2 text-accent" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div className="mt-14 mb-16 h-[2px] w-full md:mt-16 md:mb-20" style={{
        backgroundColor: "var(--border)"
      }} />

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <JournalGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}